import {
  Badge,
  Button,
  Card,
  Input,
  message,
  Popover,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Tabs,
  Empty,
  Modal,
  Tag,
  Drawer,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { formatDate } from '../../utils/format';
import { CreditCardOutlined } from '@ant-design/icons';
import InfiniteLoaderWrapper from '../InfiniteLoaderWrapper';
import useDispatchACard from '@/hooks/cards/useDispatchACard';
import { TGetTransactionResponse } from '@/types/transactions/transaction-types';
import CreateCardModal from './CreateCardModal';
import { ColumnsType } from 'antd/es/table';
import useGetCards from '@/hooks/cards/useGetCards';
import useGetProductOrders from '@/hooks/cards/useGetProductOrders';
import { CardSchema, ProductOrder } from '@/types/cards/card-types';
import { useCardFunding } from '@/hooks/cards/useCardFunding';
import CardTaggingConfirmationModal from './CardTaggingConfirmationModal';
import CardDetailsModal from './CardDetailsModal';
import BatchCardDetailsModal from './BatchCardDetailsModal';

type TCardDataProps = {
  cardData: TGetTransactionResponse[];
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  handleApplyFilter: (status: string, type: string) => void;
  isTableLoading: boolean;
};

function CardsPendingRegistrationTable({ dispatchAddressId }: { dispatchAddressId: string | null }) {
  // Tab state: 'single' or 'batch'
  const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');
  const [cardDetails, setCardDetails] = useState<CardSchema | ProductOrder | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  // Fetch single cards (pending registration or funding)
  const {
    data: singleCards = [],
    fetchNextPage: fetchNextPageSingle,
    hasNextPage: hasNextPageSingle,
    isFetchingNextPage: isFetchingNextPageSingle,
    isPending: isPendingSingle,
  } = useGetCards({ status: 'requested,pending-funding', card_type: 'physical', sort_by: 'delivery-date', ...(dispatchAddressId ? { dispatch_address_id: dispatchAddressId } : {}) });

  // Fetch batch cards (pending registration or funding)
  const {
    data: batchCards = [],
    fetchNextPage: fetchNextPageBatch,
    hasNextPage: hasNextPageBatch,
    isFetchingNextPage: isFetchingNextPageBatch,
    isPending: isPendingBatch,
  } = useGetProductOrders({ status: 'pending-product-creation', sort_by: 'delivery-date', ...(dispatchAddressId ? { dispatch_address_id: dispatchAddressId } : {}) });

  // Filtered data
  const filteredSingle: CardSchema[] = Array.isArray(singleCards)
    ? singleCards.filter((card: any) => !card.batch_id && card.id.toLowerCase().includes(searchValue.toLowerCase()))
    : [];
  const filteredBatch: ProductOrder[] = Array.isArray(batchCards)
    ? batchCards.filter((batch: any) => batch.id.toLowerCase().includes(searchValue.toLowerCase()))
    : [];

  // Sort filteredBatch by delivery_date (closest first)
  const sortedFilteredBatch = Array.isArray(filteredBatch)
    ? [...filteredBatch].sort((a, b) => {
        const dateA = (a && 'delivery_date' in a && a.delivery_date) ? new Date(a.delivery_date).getTime() : 0;
        const dateB = (b && 'delivery_date' in b && b.delivery_date) ? new Date(b.delivery_date).getTime() : 0;
        return dateA - dateB;
      })
    : [];

  // Sort filteredSingle by delivery_date (closest first)
  const sortedFilteredSingle = Array.isArray(filteredSingle)
    ? [...filteredSingle].sort((a, b) => {
        const dateA = (a && 'delivery_date' in a && a.delivery_date) ? new Date(a.delivery_date).getTime() : 0;
        const dateB = (b && 'delivery_date' in b && b.delivery_date) ? new Date(b.delivery_date).getTime() : 0;
        return dateA - dateB;
      })
    : [];

  // Helper to map CardSchema to modal fields
  function mapCardToModal(card: CardSchema | ProductOrder, type: 'single' | 'batch') {
    if (type === 'single') {
      return {
        ...card,
        reference: (card as CardSchema).id,
        dest_currency_code: (card as CardSchema).currency || '',
        dest_amount: '', // CardSchema does not have amount, leave blank or add logic if available
        disburse_recipient_name: (card as CardSchema).holder_name || '',
      };
    } else {
      // For batch, pass empty or placeholder values
      return {
        ...card,
        reference: (card as ProductOrder).id,
        dest_currency_code: '',
        dest_amount: '',
        disburse_recipient_name: '',
      };
    }
  }

  // Action handler
  const [fundCardDetails, setFundCardDetails] = useState<CardSchema | null>(null);
  const [showFundModal, setShowFundModal] = useState(false);
  const handleAction = (card: CardSchema | ProductOrder, type: 'single' | 'batch') => {
    if (card.status === 'requested') {
      setCardDetails(mapCardToModal(card, type));
      setIsModalVisible(true);
    } else if (card.status === 'pending-funding' && type === 'single') {
      setFundCardDetails(card as CardSchema);
      setShowFundModal(true);
    }
  };

  // Funding modal state and logic
  const { fundCardRequest } = useCardFunding();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loadingCardId, setLoadingCardId] = useState<string | null>(null);
  const [showTaggingModal, setShowTaggingModal] = useState(false);
  const [cardholderName, setCardholderName] = useState<string | null>(null);

  const handleConfirmFunding = async () => {
    if (!fundCardDetails) return;
    setLoadingCardId(fundCardDetails.id);
    try {
      await new Promise<void>((resolve, reject) => {
        fundCardRequest(
          {
            card_id: fundCardDetails.id,
          },
          {
            onSuccess: (response) => {
              setCardholderName(response.card_holdername || null);
              setShowTaggingModal(true);
              // Close the funding confirmation modal when tagging modal appears
              setShowConfirmationModal(false);
              resolve();
            },
            onError: reject,
          }
        );
      });
    } finally {
      setLoadingCardId(null);
      setFundCardDetails(null);
    }
  };

  // Columns for single cards (used in Drawer for batch cards)
  const singleColumns: ColumnsType<CardSchema> = [
    { title: 'Reference', dataIndex: 'id', key: 'id', width: 150 },
    { title: 'Card Holder', dataIndex: 'holder_name', key: 'holder_name', width: 200 },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 120 },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 150, render: (date: string) => date ? new Date(date).toLocaleDateString() : '-' },
    { title: 'Delivery Type', dataIndex: 'delivery_type', key: 'delivery_type', width: 120, render: (type: string) => {
      if (type === 'pickup-location') return <Tag color='orange'>PICKUP</Tag>;
      if (type === 'delivery') return <Tag color='blue'>DELIVERY</Tag>;
      return <Tag>{(type || '-').toUpperCase()}</Tag>;
    } },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record: CardSchema) => {
        if (record.status === 'requested') {
          return <Button type='primary' icon={<CreditCardOutlined />} onClick={() => handleAction(record, 'single')}>Register</Button>;
        }
        if (record.status === 'pending-funding') {
          return <Button type='primary' icon={<CreditCardOutlined />} onClick={() => handleAction(record, 'single')}>Fund</Button>;
        }
        // Show status tag for other statuses
        let color = 'default';
        let label = record.status;
        if (record.status === 'pending-collection') {
          color = 'orange';
          label = 'pending-collection';
        } else if (record.status === 'completed') {
          color = 'green';
          label = 'completed';
        }
        return <Tag color={color}>{label}</Tag>;
      }
    },
  ];

  // Columns for batch cards
  const batchColumns: ColumnsType<ProductOrder> = [
    { title: 'Reference', dataIndex: 'id', key: 'id', width: 150 },
    { title: 'Account Name', dataIndex: 'account_name', key: 'account_name', width: 200 },
    { title: 'Count', dataIndex: 'count', key: 'count', width: 120, render: (count: number) => <Tag color="blue">{count} Cards</Tag> },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 150, render: (date: string) => date ? new Date(date).toLocaleDateString() : '-' },
    { title: 'Delivery Type', dataIndex: 'delivery_type', key: 'delivery_type', width: 120, render: (type: string) => {
      if (type === 'pickup-location') return <Tag color='orange'>PICKUP</Tag>;
      if (type === 'delivery') return <Tag color='blue'>DELIVERY</Tag>;
      return <Tag>{(type || '-').toUpperCase()}</Tag>;
    } },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button onClick={() => {
          setSelectedBatch(record);
          setSelectedBatchId(record.id);
          setDrawerVisible(true);
        }}>View Cards</Button>
      ),
    },
  ];

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<ProductOrder | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [drawerCardDetails, setDrawerCardDetails] = useState<CardSchema | null>(null);
  const [drawerModalVisible, setDrawerModalVisible] = useState(false);
  const [drawerTaggingModalVisible, setDrawerTaggingModalVisible] = useState(false);
  const [drawerCardholderName, setDrawerCardholderName] = useState<string | null>(null);
  const [drawerFundingCard, setDrawerFundingCard] = useState<CardSchema | null>(null);
  const [drawerShowFundModal, setDrawerShowFundModal] = useState(false);

  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [batchDetailsModalVisible, setBatchDetailsModalVisible] = useState(false);
  const [batchDetailsCardId, setBatchDetailsCardId] = useState<string | null>(null);

  // Always call useGetCards at the top level, but only enable it when selectedBatchId is set
  const batchCardsListQuery = useGetCards(
    selectedBatchId
      ? { batch_id: selectedBatchId }
      : undefined,
    { enabled: !!selectedBatchId }
  );
  const batchCardsList: CardSchema[] = Array.isArray(batchCardsListQuery.data) ? batchCardsListQuery.data : [];
  const isFetchingBatchCardsList = batchCardsListQuery.isFetching;
  const refetchBatchCardsList = batchCardsListQuery.refetch;

  return (
    <Card
      title={<Space><Typography.Title level={5}>Pending Registrations</Typography.Title></Space>}
      style={{ marginTop: 24 }}
      extra={<Input placeholder='Search by reference or batch ID' value={searchValue} onChange={e => setSearchValue(e.target.value)} style={{ width: '100%' }} />}
    >
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'single' | 'batch')}
        items={[{
          key: 'single',
          label: 'Single Cards',
          children: (
            <InfiniteLoaderWrapper
              hasNextPage={hasNextPageSingle}
              fetchNextPage={fetchNextPageSingle}
              isFetchingNextPage={isFetchingNextPageSingle}
            >
              <Table
                dataSource={sortedFilteredSingle}
                columns={singleColumns}
                rowKey="id"
                pagination={false}
                loading={isPendingSingle}
                onRow={(record) => ({
                  onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
                    // Prevent modal if action button is clicked
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('button') ||
                      target.closest('a')
                    ) {
                      return;
                    }
                    setSelectedCardId(record.id);
                    setDetailsModalVisible(true);
                  },
                  style: { cursor: 'pointer' },
                })}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No pending registrations or fundings' /> }}
              />
            </InfiniteLoaderWrapper>
          ),
        }, {
          key: 'batch',
          label: 'Batch Cards',
          children: (
            <InfiniteLoaderWrapper
              hasNextPage={hasNextPageBatch}
              fetchNextPage={fetchNextPageBatch}
              isFetchingNextPage={isFetchingNextPageBatch}
            >
              <Table
                dataSource={sortedFilteredBatch}
                columns={batchColumns}
                rowKey='id'
                pagination={false}
                loading={isPendingBatch}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No pending registrations or fundings' /> }}
              />
            </InfiniteLoaderWrapper>
          ),
        }]}
        size='large'
        type='card'
      />
      <CreateCardModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        cardData={cardDetails as any}
        zIndex={2000}
      />
      {/* Fund Card Modal */}
      {fundCardDetails && (
        <Modal
          title="Confirm Card Funding"
          open={showFundModal}
          onOk={handleConfirmFunding}
          onCancel={() => {
            setShowFundModal(false);
            setFundCardDetails(null);
          }}
          confirmLoading={!!loadingCardId}
        >
          <p>Are you sure you want to fund this card?</p>
          <div>
            <p>Card Number: **** **** **** {fundCardDetails.last_four}</p>
            <p>Brand: {fundCardDetails.brand?.toUpperCase()}</p>
          </div>
        </Modal>
      )}
      <CardTaggingConfirmationModal
        open={showTaggingModal}
        cardholderNames={cardholderName ? [cardholderName] : []}
        onConfirm={() => {
          setShowTaggingModal(false);
          setCardholderName(null);
        }}
      />
      <CardDetailsModal
        isModalVisible={detailsModalVisible}
        setIsModalVisible={setDetailsModalVisible}
        cardId={selectedCardId}
      />
      {drawerVisible && selectedBatchId && (
        <Drawer
          title={selectedBatch ? `Cards in Batch: ${selectedBatch.id}` : 'Cards in Batch'}
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          width={900}
          destroyOnClose
        >
          <Table
            dataSource={batchCardsList}
            columns={singleColumns}
            rowKey="id"
            pagination={false}
            loading={isFetchingBatchCardsList}
            onRow={(record) => ({
              onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
                const target = e.target as HTMLElement;
                if (
                  target.closest('button') ||
                  target.closest('a')
                ) {
                  return;
                }
                setBatchDetailsCardId(record.id);
                setBatchDetailsModalVisible(true);
              },
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No cards in this batch' /> }}
          />
          <BatchCardDetailsModal
            visible={batchDetailsModalVisible}
            onClose={() => setBatchDetailsModalVisible(false)}
            cardId={batchDetailsCardId}
            cardData={batchCardsList.find(card => card.id === batchDetailsCardId)}
            batchId={selectedBatchId}
            showCardNumber={false}
          />
        </Drawer>
      )}
    </Card>
  );
}

export default CardsPendingRegistrationTable;
